import { motion } from "framer-motion";
import { springs } from "../ui/motion";

function getUrgencyConfig(days: number) {
  if (days <= 0)
    return {
      color: "#ef4444",
      bg: "rgba(239,68,68,0.06)",
      border: "rgba(239,68,68,0.20)",
      label: "Expired",
      pulse: true,
      ring: 1,
    };
  if (days <= 3)
    return {
      color: "#ef4444",
      bg: "rgba(239,68,68,0.06)",
      border: "rgba(239,68,68,0.20)",
      label: `${days}d remaining`,
      pulse: true,
      ring: days / 7,
    };
  if (days <= 7)
    return {
      color: "#f59e0b",
      bg: "rgba(245,158,11,0.06)",
      border: "rgba(245,158,11,0.20)",
      label: `${days} days remaining`,
      pulse: false,
      ring: days / 30,
    };
  return {
    color: "#3b82f6",
    bg: "rgba(59,130,246,0.06)",
    border: "rgba(59,130,246,0.20)",
    label: `${days} days remaining`,
    pulse: false,
    ring: days / 90,
  };
}

interface Props {
  daysRemaining: number;
  expirationDate?: string;
}

export function DocumentExpirationWidget({
  daysRemaining,
  expirationDate,
}: Props) {
  const urgency = getUrgencyConfig(daysRemaining);
  // Clamp ring progress between 0.05 and 1
  const ringProgress = Math.max(0.05, Math.min(1, urgency.ring));
  // SVG circle math: radius 18, circumference ~113
  const circumference = 2 * Math.PI * 18;
  const strokeDashoffset = circumference * (1 - ringProgress);

  return (
    <motion.div
      className="relative rounded-[12px] border p-4 mb-4 overflow-hidden"
      style={{
        borderColor: urgency.border,
        backgroundColor: urgency.bg,
      }}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ ...springs.smooth, delay: 0.1 }}
    >
      <div className="flex items-center gap-4">
        {/* Countdown ring */}
        <div className="relative w-12 h-12 shrink-0">
          <svg
            viewBox="0 0 40 40"
            className="w-full h-full -rotate-90"
            role="img"
            aria-label="Document expiration countdown"
          >
            {/* Track */}
            <circle
              cx="20"
              cy="20"
              r="18"
              fill="none"
              stroke={`${urgency.color}15`}
              strokeWidth="2.5"
            />
            {/* Progress arc */}
            <motion.circle
              cx="20"
              cy="20"
              r="18"
              fill="none"
              stroke={urgency.color}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeDasharray={circumference}
              initial={{ strokeDashoffset: circumference }}
              animate={{ strokeDashoffset }}
              transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            />
          </svg>

          {/* Center number */}
          <motion.div
            className="absolute inset-0 flex items-center justify-center"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ ...springs.bouncy, delay: 0.25 }}
          >
            <span
              className="text-[13px] font-bold"
              style={{ color: urgency.color }}
            >
              {daysRemaining <= 0 ? "!" : daysRemaining}
            </span>
          </motion.div>

          {/* Pulse ring for urgent */}
          {urgency.pulse && (
            <motion.div
              className="absolute inset-0 rounded-full"
              style={{ border: `2px solid ${urgency.color}` }}
              animate={{ scale: [1, 1.3], opacity: [0.4, 0] }}
              transition={{
                duration: 1.5,
                repeat: Number.POSITIVE_INFINITY,
                ease: "easeOut",
              }}
            />
          )}
        </div>

        {/* Text */}
        <div className="flex-1 min-w-0">
          <p
            className="text-[13px] font-semibold"
            style={{ color: urgency.color }}
          >
            {urgency.label}
          </p>
          {expirationDate && (
            <p className="text-[12px] text-muted mt-0.5">
              {new Date(expirationDate).toLocaleDateString("en-GB", {
                weekday: "short",
                day: "numeric",
                month: "short",
                year: "numeric",
              })}
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
}
